"use client";

import { c as _c } from "react/compiler-runtime";
import type React from "react";
import { useCallback, useRef, useState } from "react";
import { flushSync } from "react-dom";
import type SwiperClass from "swiper";
import { usePatchComputedStyleForFallbackSwipe, useQuickVotePreviewCardTouchFallback, useSwipeCommitTimeoutCleanup } from "./useMemesQuickVotePreviewSwipe.helpers";
const SWIPE_TRIGGER_THRESHOLD = 32;
const MAX_SWIPE_OFFSET = 132;
const SWIPE_EXIT_DURATION_MS = 220;
const SWIPE_EXIT_OFFSET = 420;
const SWIPE_EXIT_ROTATION_DEGREES = 3;
const TOUCH_GESTURE_AXIS_LOCK_THRESHOLD = 8;
type TimeoutHandle = ReturnType<typeof globalThis.setTimeout>;
type SwipeDirection = "left" | "right";
type TouchGestureAxis = "pending" | "horizontal" | "vertical";
type TouchLikeEvent = {
  readonly changedTouches?: ArrayLike<{
    readonly clientX?: number;
    readonly clientY?: number;
    readonly pageX?: number;
    readonly pageY?: number;
  }> | undefined;
  readonly targetTouches?: ArrayLike<{
    readonly clientX?: number;
    readonly clientY?: number;
    readonly pageX?: number;
    readonly pageY?: number;
  }> | undefined;
  readonly touches?: ArrayLike<{
    readonly clientX?: number;
    readonly clientY?: number;
    readonly pageX?: number;
    readonly pageY?: number;
  }> | undefined;
  readonly stopPropagation?: () => void;
};
interface UseMemesQuickVotePreviewSwipeArgs {
  readonly isBusy: boolean;
  readonly isMobile: boolean;
  readonly onAdvanceStart: () => void;
  readonly onSkip: () => void;
  readonly onVoteWithSwipe: () => void;
  readonly swipeVoteAmount: number | null;
}
interface UseMemesQuickVotePreviewSwipeResult {
  readonly previewCardRef: React.RefObject<HTMLElement | null>;
  readonly canUseSwiperTouchSurface: boolean;
  readonly cardTransform: string | undefined;
  readonly cardTransitionDuration: string;
  readonly handleTouchSurfaceCancel: (event: TouchLikeEvent) => void;
  readonly handleTouchSurfaceEnd: (event: TouchLikeEvent) => void;
  readonly handleTouchSurfaceMove: (event: TouchLikeEvent) => void;
  readonly handleTouchSurfaceStart: (event: TouchLikeEvent) => void;
  readonly swipeOffset: number;
  readonly handleCardTransitionEnd: (event: React.TransitionEvent<HTMLElement>) => void;
  readonly handleSwiperMove: (swiper: SwiperClass) => void;
  readonly handleSwiperTouchEnd: (swiper: SwiperClass) => void;
}
interface SwipeState {
  readonly handleCardTransitionEnd: (event: React.TransitionEvent<HTMLElement>) => void;
  readonly handleTouchSurfaceCancel: (event: TouchLikeEvent) => void;
  readonly handleTouchSurfaceEnd: (event: TouchLikeEvent) => void;
  readonly handleTouchSurfaceMove: (event: TouchLikeEvent) => void;
  readonly handleTouchSurfaceStart: (event: TouchLikeEvent) => void;
  readonly handleSwiperMove: (swiper: SwiperClass) => void;
  readonly handleSwiperTouchEnd: (swiper: SwiperClass) => void;
  readonly swipeExitDirection: SwipeDirection | null;
  readonly swipeOffset: number;
}
interface TouchSurfaceHandlerArgs {
  readonly fallbackTouchAxisRef: React.RefObject<TouchGestureAxis>;
  readonly fallbackTouchCurrentXRef: React.RefObject<number | null>;
  readonly fallbackTouchCurrentYRef: React.RefObject<number | null>;
  readonly fallbackTouchStartXRef: React.RefObject<number | null>;
  readonly fallbackTouchStartYRef: React.RefObject<number | null>;
  readonly handleSwipeEnd: (swipeDistance: number) => void;
  readonly isBusy: boolean;
  readonly isFallbackTouchActiveRef: React.RefObject<boolean>;
  readonly resetFallbackTouch: () => void;
  readonly resetSwipe: () => void;
  readonly setSwipeOffset: React.Dispatch<React.SetStateAction<number>>;
  readonly swipeExitDirection: SwipeDirection | null;
}
interface TouchSurfaceCompletionHandlerArgs {
  readonly fallbackTouchAxisRef: React.RefObject<TouchGestureAxis>;
  readonly fallbackTouchCurrentXRef: React.RefObject<number | null>;
  readonly fallbackTouchStartXRef: React.RefObject<number | null>;
  readonly handleSwipeEnd: (swipeDistance: number) => void;
  readonly isBusy: boolean;
  readonly isFallbackTouchActiveRef: React.RefObject<boolean>;
  readonly resetFallbackTouch: () => void;
  readonly resetSwipe: () => void;
}
function commitSwipeAction(direction: SwipeDirection, onSkip: () => void, onVoteWithSwipe: () => void) {
  if (direction === "left") {
    onSkip();
    return;
  }
  onVoteWithSwipe();
}
const getTouchClientX = (touch: {
  readonly clientX?: number;
  readonly clientY?: number;
  readonly pageX?: number;
  readonly pageY?: number;
} | undefined): number | null => {
  if (typeof touch?.pageX === "number") {
    return touch.pageX;
  }
  if (typeof touch?.clientX === "number") {
    return touch.clientX;
  }
  return null;
};
const getTouchClientY = (touch: {
  readonly clientX?: number;
  readonly clientY?: number;
  readonly pageX?: number;
  readonly pageY?: number;
} | undefined): number | null => {
  if (typeof touch?.pageY === "number") {
    return touch.pageY;
  }
  if (typeof touch?.clientY === "number") {
    return touch.clientY;
  }
  return null;
};
const getTouchListClientX = (touches: ArrayLike<{
  readonly clientX?: number;
  readonly clientY?: number;
  readonly pageX?: number;
  readonly pageY?: number;
}> | undefined): number | null => {
  if (!touches || touches.length === 0) {
    return null;
  }
  return getTouchClientX(touches[0]);
};
const getTouchListClientY = (touches: ArrayLike<{
  readonly clientX?: number;
  readonly clientY?: number;
  readonly pageX?: number;
  readonly pageY?: number;
}> | undefined): number | null => {
  if (!touches || touches.length === 0) {
    return null;
  }
  return getTouchClientY(touches[0]);
};
const getTouchEventClientX = (event: TouchLikeEvent): number | null => getTouchListClientX(event.touches) ?? getTouchListClientX(event.targetTouches) ?? getTouchListClientX(event.changedTouches);
const getTouchEventClientY = (event: TouchLikeEvent): number | null => getTouchListClientY(event.touches) ?? getTouchListClientY(event.targetTouches) ?? getTouchListClientY(event.changedTouches);
const clampSwipeOffset = (swipeOffset: number) => Math.max(-MAX_SWIPE_OFFSET, Math.min(swipeOffset, MAX_SWIPE_OFFSET));
function startTouchSurfaceTracking({
  fallbackTouchAxisRef,
  fallbackTouchCurrentXRef,
  fallbackTouchCurrentYRef,
  fallbackTouchStartXRef,
  fallbackTouchStartYRef,
  isFallbackTouchActiveRef,
  setSwipeOffset,
  startX,
  startY
}: {
  readonly fallbackTouchAxisRef: React.RefObject<TouchGestureAxis>;
  readonly fallbackTouchCurrentXRef: React.RefObject<number | null>;
  readonly fallbackTouchCurrentYRef: React.RefObject<number | null>;
  readonly fallbackTouchStartXRef: React.RefObject<number | null>;
  readonly fallbackTouchStartYRef: React.RefObject<number | null>;
  readonly isFallbackTouchActiveRef: React.RefObject<boolean>;
  readonly setSwipeOffset: React.Dispatch<React.SetStateAction<number>>;
  readonly startX: number;
  readonly startY: number;
}) {
  fallbackTouchAxisRef.current = "pending";
  fallbackTouchStartXRef.current = startX;
  fallbackTouchStartYRef.current = startY;
  fallbackTouchCurrentXRef.current = startX;
  fallbackTouchCurrentYRef.current = startY;
  isFallbackTouchActiveRef.current = true;
  flushSync(() => {
    setSwipeOffset(0);
  });
}
function resolveTouchSurfaceOffset({
  currentX,
  currentY,
  fallbackTouchAxisRef,
  fallbackTouchCurrentXRef,
  fallbackTouchCurrentYRef,
  startX,
  startY
}: {
  readonly currentX: number;
  readonly currentY: number;
  readonly fallbackTouchAxisRef: React.RefObject<TouchGestureAxis>;
  readonly fallbackTouchCurrentXRef: React.RefObject<number | null>;
  readonly fallbackTouchCurrentYRef: React.RefObject<number | null>;
  readonly startX: number;
  readonly startY: number;
}): number | null {
  fallbackTouchCurrentXRef.current = currentX;
  fallbackTouchCurrentYRef.current = currentY;
  const deltaX = currentX - startX;
  const deltaY = currentY - startY;
  const absoluteDeltaX = Math.abs(deltaX);
  const absoluteDeltaY = Math.abs(deltaY);
  if (fallbackTouchAxisRef.current === "pending") {
    if (absoluteDeltaX < TOUCH_GESTURE_AXIS_LOCK_THRESHOLD && absoluteDeltaY < TOUCH_GESTURE_AXIS_LOCK_THRESHOLD) {
      return null;
    }
    fallbackTouchAxisRef.current = absoluteDeltaX > absoluteDeltaY ? "horizontal" : "vertical";
  }
  if (fallbackTouchAxisRef.current !== "horizontal") {
    return null;
  }
  return clampSwipeOffset(deltaX);
}
function getTouchSurfaceDistance({
  fallbackTouchAxisRef,
  startX,
  endX
}: {
  readonly fallbackTouchAxisRef: React.RefObject<TouchGestureAxis>;
  readonly startX: number | null;
  readonly endX: number | null;
}): number | null {
  if (fallbackTouchAxisRef.current !== "horizontal" || startX === null || endX === null) {
    return null;
  }
  return endX - startX;
}
function getCardTransform(isMobile: boolean, swipeExitDirection: SwipeDirection | null, swipeOffset: number) {
  if (!isMobile) {
    return undefined;
  }
  if (swipeExitDirection === "left") {
    return `translateX(${Math.min(swipeOffset, 0) - SWIPE_EXIT_OFFSET}px) rotate(-${SWIPE_EXIT_ROTATION_DEGREES}deg)`;
  }
  if (swipeExitDirection === "right") {
    return `translateX(${Math.max(swipeOffset, 0) + SWIPE_EXIT_OFFSET}px) rotate(${SWIPE_EXIT_ROTATION_DEGREES}deg)`;
  }
  return `translateX(${swipeOffset}px)`;
}
function getCardTransitionDuration(swipeExitDirection: SwipeDirection | null, swipeOffset: number) {
  if (swipeExitDirection === null && swipeOffset !== 0) {
    return "0ms";
  }
  return `${SWIPE_EXIT_DURATION_MS}ms`;
}
function useQuickVoteTouchSurfaceHandlers(t0) {
  const $ = _c(34);
  const {
    fallbackTouchAxisRef,
    fallbackTouchCurrentXRef,
    fallbackTouchCurrentYRef,
    fallbackTouchStartXRef,
    fallbackTouchStartYRef,
    handleSwipeEnd,
    isBusy,
    isFallbackTouchActiveRef,
    resetFallbackTouch,
    resetSwipe,
    setSwipeOffset,
    swipeExitDirection
  } = t0;
  const touchAxisRef = fallbackTouchAxisRef;
  const touchCurrentXRef = fallbackTouchCurrentXRef;
  const touchCurrentYRef = fallbackTouchCurrentYRef;
  const touchStartXRef = fallbackTouchStartXRef;
  const touchStartYRef = fallbackTouchStartYRef;
  const isTouchActiveRef = isFallbackTouchActiveRef;
  let t1;
  if ($[0] !== handleSwipeEnd || $[1] !== isBusy || $[2] !== isTouchActiveRef || $[3] !== resetFallbackTouch || $[4] !== resetSwipe || $[5] !== touchAxisRef || $[6] !== touchCurrentXRef || $[7] !== touchStartXRef) {
    t1 = {
      fallbackTouchAxisRef: touchAxisRef,
      fallbackTouchCurrentXRef: touchCurrentXRef,
      fallbackTouchStartXRef: touchStartXRef,
      handleSwipeEnd,
      isBusy,
      isFallbackTouchActiveRef: isTouchActiveRef,
      resetFallbackTouch,
      resetSwipe
    };
    $[0] = handleSwipeEnd;
    $[1] = isBusy;
    $[2] = isTouchActiveRef;
    $[3] = resetFallbackTouch;
    $[4] = resetSwipe;
    $[5] = touchAxisRef;
    $[6] = touchCurrentXRef;
    $[7] = touchStartXRef;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const {
    handleTouchSurfaceCancel,
    handleTouchSurfaceEnd
  } = useQuickVoteTouchSurfaceCompletionHandlers(t1);
  let t2;
  if ($[9] !== isBusy || $[10] !== isTouchActiveRef || $[11] !== resetFallbackTouch || $[12] !== setSwipeOffset || $[13] !== swipeExitDirection || $[14] !== touchAxisRef || $[15] !== touchCurrentXRef || $[16] !== touchCurrentYRef || $[17] !== touchStartXRef || $[18] !== touchStartYRef) {
    t2 = event => {
      if (isBusy || swipeExitDirection !== null) {
        resetFallbackTouch();
        return;
      }
      if (isTouchActiveRef.current) {
        return;
      }
      const startX = getTouchEventClientX(event);
      const startY = getTouchEventClientY(event);
      if (startX === null || startY === null) {
        return;
      }
      event.stopPropagation?.();
      startTouchSurfaceTracking({
        fallbackTouchAxisRef: touchAxisRef,
        fallbackTouchCurrentXRef: touchCurrentXRef,
        fallbackTouchCurrentYRef: touchCurrentYRef,
        fallbackTouchStartXRef: touchStartXRef,
        fallbackTouchStartYRef: touchStartYRef,
        isFallbackTouchActiveRef: isTouchActiveRef,
        setSwipeOffset,
        startX,
        startY
      });
    };
    $[9] = isBusy;
    $[10] = isTouchActiveRef;
    $[11] = resetFallbackTouch;
    $[12] = setSwipeOffset;
    $[13] = swipeExitDirection;
    $[14] = touchAxisRef;
    $[15] = touchCurrentXRef;
    $[16] = touchCurrentYRef;
    $[17] = touchStartXRef;
    $[18] = touchStartYRef;
    $[19] = t2;
  } else {
    t2 = $[19];
  }
  const handleTouchSurfaceStart = t2;
  let t3;
  if ($[20] !== isTouchActiveRef || $[21] !== setSwipeOffset || $[22] !== swipeExitDirection || $[23] !== touchAxisRef || $[24] !== touchCurrentXRef || $[25] !== touchCurrentYRef || $[26] !== touchStartXRef || $[27] !== touchStartYRef) {
    t3 = event_0 => {
      if (!isTouchActiveRef.current || swipeExitDirection !== null) {
        return;
      }
      const startX_0 = touchStartXRef.current;
      const startY_0 = touchStartYRef.current;
      const currentX = getTouchEventClientX(event_0);
      const currentY = getTouchEventClientY(event_0);
      if (startX_0 === null || startY_0 === null || currentX === null || currentY === null) {
        return;
      }
      const nextSwipeOffset = resolveTouchSurfaceOffset({
        currentX,
        currentY,
        fallbackTouchAxisRef: touchAxisRef,
        fallbackTouchCurrentXRef: touchCurrentXRef,
        fallbackTouchCurrentYRef: touchCurrentYRef,
        startX: startX_0,
        startY: startY_0
      });
      if (nextSwipeOffset === null) {
        return;
      }
      event_0.stopPropagation?.();
      flushSync(() => {
        setSwipeOffset(nextSwipeOffset);
      });
    };
    $[20] = isTouchActiveRef;
    $[21] = setSwipeOffset;
    $[22] = swipeExitDirection;
    $[23] = touchAxisRef;
    $[24] = touchCurrentXRef;
    $[25] = touchCurrentYRef;
    $[26] = touchStartXRef;
    $[27] = touchStartYRef;
    $[28] = t3;
  } else {
    t3 = $[28];
  }
  const handleTouchSurfaceMove = t3;
  let t4;
  if ($[29] !== handleTouchSurfaceCancel || $[30] !== handleTouchSurfaceEnd || $[31] !== handleTouchSurfaceMove || $[32] !== handleTouchSurfaceStart) {
    t4 = {
      handleTouchSurfaceCancel,
      handleTouchSurfaceEnd,
      handleTouchSurfaceMove,
      handleTouchSurfaceStart
    };
    $[29] = handleTouchSurfaceCancel;
    $[30] = handleTouchSurfaceEnd;
    $[31] = handleTouchSurfaceMove;
    $[32] = handleTouchSurfaceStart;
    $[33] = t4;
  } else {
    t4 = $[33];
  }
  return t4;
}
function useQuickVoteTouchSurfaceCompletionHandlers(t0) {
  const $ = _c(16);
  const {
    fallbackTouchAxisRef,
    fallbackTouchCurrentXRef,
    fallbackTouchStartXRef,
    handleSwipeEnd,
    isBusy,
    isFallbackTouchActiveRef,
    resetFallbackTouch,
    resetSwipe
  } = t0;
  let t1;
  if ($[0] !== fallbackTouchAxisRef || $[1] !== fallbackTouchCurrentXRef || $[2] !== fallbackTouchStartXRef || $[3] !== handleSwipeEnd || $[4] !== isBusy || $[5] !== isFallbackTouchActiveRef || $[6] !== resetFallbackTouch || $[7] !== resetSwipe) {
    t1 = event => {
      if (!isFallbackTouchActiveRef.current) {
        return;
      }
      event.stopPropagation?.();
      if (isBusy) {
        resetFallbackTouch();
        resetSwipe();
        return;
      }
      const swipeDistance = getTouchSurfaceDistance({
        fallbackTouchAxisRef,
        startX: fallbackTouchStartXRef.current,
        endX: getTouchEventClientX(event) ?? fallbackTouchCurrentXRef.current
      });
      if (swipeDistance === null) {
        resetFallbackTouch();
        resetSwipe();
        return;
      }
      handleSwipeEnd(swipeDistance);
    };
    $[0] = fallbackTouchAxisRef;
    $[1] = fallbackTouchCurrentXRef;
    $[2] = fallbackTouchStartXRef;
    $[3] = handleSwipeEnd;
    $[4] = isBusy;
    $[5] = isFallbackTouchActiveRef;
    $[6] = resetFallbackTouch;
    $[7] = resetSwipe;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const handleTouchSurfaceEnd = t1;
  let t2;
  if ($[9] !== isFallbackTouchActiveRef || $[10] !== resetFallbackTouch || $[11] !== resetSwipe) {
    t2 = event_0 => {
      if (!isFallbackTouchActiveRef.current) {
        return;
      }
      event_0.stopPropagation?.();
      resetFallbackTouch();
      resetSwipe();
    };
    $[9] = isFallbackTouchActiveRef;
    $[10] = resetFallbackTouch;
    $[11] = resetSwipe;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const handleTouchSurfaceCancel = t2;
  let t3;
  if ($[13] !== handleTouchSurfaceCancel || $[14] !== handleTouchSurfaceEnd) {
    t3 = {
      handleTouchSurfaceCancel,
      handleTouchSurfaceEnd
    };
    $[13] = handleTouchSurfaceCancel;
    $[14] = handleTouchSurfaceEnd;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  return t3;
}
function useQuickVoteSwiperHandlers(t0) {
  const $ = _c(13);
  const {
    handleSwipeEnd,
    isBusy,
    isMobile,
    resetSwipe,
    setSwipeOffset,
    swipeExitDirection
  } = t0;
  let t1;
  if ($[0] !== isBusy || $[1] !== isMobile || $[2] !== setSwipeOffset || $[3] !== swipeExitDirection) {
    t1 = swiper => {
      if (isBusy || !isMobile || swipeExitDirection !== null) {
        return;
      }
      setSwipeOffset(clampSwipeOffset(swiper.touches.diff));
    };
    $[0] = isBusy;
    $[1] = isMobile;
    $[2] = setSwipeOffset;
    $[3] = swipeExitDirection;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const handleSwiperMove = t1;
  let t2;
  if ($[5] !== handleSwipeEnd || $[6] !== isBusy || $[7] !== isMobile || $[8] !== resetSwipe) {
    t2 = swiper_0 => {
      if (isBusy || !isMobile) {
        resetSwipe();
        return;
      }
      handleSwipeEnd(swiper_0.touches.diff);
    };
    $[5] = handleSwipeEnd;
    $[6] = isBusy;
    $[7] = isMobile;
    $[8] = resetSwipe;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const handleSwiperTouchEnd = t2;
  let t3;
  if ($[10] !== handleSwiperMove || $[11] !== handleSwiperTouchEnd) {
    t3 = {
      handleSwiperMove,
      handleSwiperTouchEnd
    };
    $[10] = handleSwiperMove;
    $[11] = handleSwiperTouchEnd;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
}
function useQuickVotePreviewSwipeState(t0) {
  const $ = _c(40);
  const {
    isBusy,
    isMobile,
    onAdvanceStart,
    onSkip,
    onVoteWithSwipe,
    swipeVoteAmount,
    usesTransitionlessSwipeCommit
  } = t0;
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [swipeExitDirection, setSwipeExitDirection] = useState(null);
  const fallbackTouchAxisRef = useRef("pending");
  const fallbackTouchStartXRef = useRef(null);
  const fallbackTouchStartYRef = useRef(null);
  const fallbackTouchCurrentXRef = useRef(null);
  const fallbackTouchCurrentYRef = useRef(null);
  const isFallbackTouchActiveRef = useRef(false);
  const swipeCommitTimeoutRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (swipeCommitTimeoutRef.current === null) {
        return;
      }
      globalThis.clearTimeout(swipeCommitTimeoutRef.current);
      swipeCommitTimeoutRef.current = null;
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const clearSwipeCommitTimeout = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      fallbackTouchAxisRef.current = "pending";
      fallbackTouchStartXRef.current = null;
      fallbackTouchStartYRef.current = null;
      fallbackTouchCurrentXRef.current = null;
      fallbackTouchCurrentYRef.current = null;
      isFallbackTouchActiveRef.current = false;
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const resetFallbackTouch = t2;
  let t3;
  if ($[2] !== swipeExitDirection) {
    t3 = () => {
      if (swipeExitDirection !== null) {
        return;
      }
      flushSync(() => {
        setSwipeOffset(0);
      });
    };
    $[2] = swipeExitDirection;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const resetSwipe = t3;
  let t4;
  if ($[4] !== onAdvanceStart || $[5] !== onSkip || $[6] !== onVoteWithSwipe || $[7] !== swipeExitDirection || $[8] !== usesTransitionlessSwipeCommit) {
    t4 = direction => {
      if (swipeExitDirection !== null) {
        return;
      }
      clearSwipeCommitTimeout();
      resetFallbackTouch();
      onAdvanceStart();
      flushSync(() => {
        setSwipeExitDirection(direction);
      });
      if (!usesTransitionlessSwipeCommit) {
        return;
      }
      swipeCommitTimeoutRef.current = globalThis.setTimeout(() => {
        swipeCommitTimeoutRef.current = null;
        commitSwipeAction(direction, onSkip, onVoteWithSwipe);
      }, SWIPE_EXIT_DURATION_MS);
    };
    $[4] = onAdvanceStart;
    $[5] = onSkip;
    $[6] = onVoteWithSwipe;
    $[7] = swipeExitDirection;
    $[8] = usesTransitionlessSwipeCommit;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const beginSwipeCommit = t4;
  let t5;
  if ($[10] !== beginSwipeCommit || $[11] !== resetSwipe || $[12] !== swipeVoteAmount) {
    t5 = swipeDistance => {
      resetFallbackTouch();
      if (swipeDistance <= -SWIPE_TRIGGER_THRESHOLD) {
        beginSwipeCommit("left");
        return;
      }
      if (swipeDistance >= SWIPE_TRIGGER_THRESHOLD && swipeVoteAmount !== null) {
        beginSwipeCommit("right");
        return;
      }
      resetSwipe();
    };
    $[10] = beginSwipeCommit;
    $[11] = resetSwipe;
    $[12] = swipeVoteAmount;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const handleSwipeEnd = t5;
  let t6;
  if ($[14] !== handleSwipeEnd || $[15] !== isBusy || $[16] !== resetSwipe || $[17] !== swipeExitDirection) {
    t6 = {
      fallbackTouchAxisRef,
      fallbackTouchCurrentXRef,
      fallbackTouchCurrentYRef,
      fallbackTouchStartXRef,
      fallbackTouchStartYRef,
      handleSwipeEnd,
      isBusy,
      isFallbackTouchActiveRef,
      resetFallbackTouch,
      resetSwipe,
      setSwipeOffset,
      swipeExitDirection
    };
    $[14] = handleSwipeEnd;
    $[15] = isBusy;
    $[16] = resetSwipe;
    $[17] = swipeExitDirection;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const {
    handleTouchSurfaceCancel,
    handleTouchSurfaceEnd,
    handleTouchSurfaceMove,
    handleTouchSurfaceStart
  } = useQuickVoteTouchSurfaceHandlers(t6);
  let t7;
  if ($[19] !== handleSwipeEnd || $[20] !== isBusy || $[21] !== isMobile || $[22] !== resetSwipe || $[23] !== swipeExitDirection) {
    t7 = {
      handleSwipeEnd,
      isBusy,
      isMobile,
      resetSwipe,
      setSwipeOffset,
      swipeExitDirection
    };
    $[19] = handleSwipeEnd;
    $[20] = isBusy;
    $[21] = isMobile;
    $[22] = resetSwipe;
    $[23] = swipeExitDirection;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  const {
    handleSwiperMove,
    handleSwiperTouchEnd
  } = useQuickVoteSwiperHandlers(t7);
  let t8;
  if ($[25] !== onSkip || $[26] !== onVoteWithSwipe || $[27] !== swipeExitDirection || $[28] !== usesTransitionlessSwipeCommit) {
    t8 = event => {
      if (event.target !== event.currentTarget || event.propertyName !== "transform" || swipeExitDirection === null) {
        return;
      }
      if (usesTransitionlessSwipeCommit) {
        return;
      }
      commitSwipeAction(swipeExitDirection, onSkip, onVoteWithSwipe);
    };
    $[25] = onSkip;
    $[26] = onVoteWithSwipe;
    $[27] = swipeExitDirection;
    $[28] = usesTransitionlessSwipeCommit;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  const handleCardTransitionEnd = t8;
  useSwipeCommitTimeoutCleanup(clearSwipeCommitTimeout);
  let t9;
  if ($[30] !== handleCardTransitionEnd || $[31] !== handleSwiperMove || $[32] !== handleSwiperTouchEnd || $[33] !== handleTouchSurfaceCancel || $[34] !== handleTouchSurfaceEnd || $[35] !== handleTouchSurfaceMove || $[36] !== handleTouchSurfaceStart || $[37] !== swipeExitDirection || $[38] !== swipeOffset) {
    t9 = {
      handleCardTransitionEnd,
      handleTouchSurfaceCancel,
      handleTouchSurfaceEnd,
      handleTouchSurfaceMove,
      handleTouchSurfaceStart,
      handleSwiperMove,
      handleSwiperTouchEnd,
      swipeExitDirection,
      swipeOffset
    };
    $[30] = handleCardTransitionEnd;
    $[31] = handleSwiperMove;
    $[32] = handleSwiperTouchEnd;
    $[33] = handleTouchSurfaceCancel;
    $[34] = handleTouchSurfaceEnd;
    $[35] = handleTouchSurfaceMove;
    $[36] = handleTouchSurfaceStart;
    $[37] = swipeExitDirection;
    $[38] = swipeOffset;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  return t9;
}
export default function useMemesQuickVotePreviewSwipe(t0) {
  const $ = _c(34);
  const {
    isBusy,
    isMobile,
    onAdvanceStart,
    onSkip,
    onVoteWithSwipe,
    swipeVoteAmount
  } = t0;
  const previewCardRef = useRef(null);
  const canUseSwiperTouchSurface = isMobile && globalThis.window !== undefined && typeof globalThis.window.Touch === "function";
  const t1 = isMobile && !canUseSwiperTouchSurface;
  let t2;
  if ($[0] !== isBusy || $[1] !== isMobile || $[2] !== onAdvanceStart || $[3] !== onSkip || $[4] !== onVoteWithSwipe || $[5] !== swipeVoteAmount || $[6] !== t1) {
    t2 = {
      isBusy,
      isMobile,
      onAdvanceStart,
      onSkip,
      onVoteWithSwipe,
      swipeVoteAmount,
      usesTransitionlessSwipeCommit: t1
    };
    $[0] = isBusy;
    $[1] = isMobile;
    $[2] = onAdvanceStart;
    $[3] = onSkip;
    $[4] = onVoteWithSwipe;
    $[5] = swipeVoteAmount;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const swipeState = useQuickVotePreviewSwipeState(t2);
  usePatchComputedStyleForFallbackSwipe();
  let t3;
  if ($[8] !== canUseSwiperTouchSurface || $[9] !== isMobile || $[10] !== swipeState.handleTouchSurfaceCancel || $[11] !== swipeState.handleTouchSurfaceEnd || $[12] !== swipeState.handleTouchSurfaceMove || $[13] !== swipeState.handleTouchSurfaceStart) {
    t3 = {
      canUseSwiperTouchSurface,
      handleTouchSurfaceCancel: swipeState.handleTouchSurfaceCancel,
      handleTouchSurfaceEnd: swipeState.handleTouchSurfaceEnd,
      handleTouchSurfaceMove: swipeState.handleTouchSurfaceMove,
      handleTouchSurfaceStart: swipeState.handleTouchSurfaceStart,
      isMobile,
      previewCardRef
    };
    $[8] = canUseSwiperTouchSurface;
    $[9] = isMobile;
    $[10] = swipeState.handleTouchSurfaceCancel;
    $[11] = swipeState.handleTouchSurfaceEnd;
    $[12] = swipeState.handleTouchSurfaceMove;
    $[13] = swipeState.handleTouchSurfaceStart;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  useQuickVotePreviewCardTouchFallback(t3);
  let t4;
  if ($[15] !== isMobile || $[16] !== swipeState.swipeExitDirection || $[17] !== swipeState.swipeOffset) {
    t4 = getCardTransform(isMobile, swipeState.swipeExitDirection, swipeState.swipeOffset);
    $[15] = isMobile;
    $[16] = swipeState.swipeExitDirection;
    $[17] = swipeState.swipeOffset;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== swipeState.swipeExitDirection || $[20] !== swipeState.swipeOffset) {
    t5 = getCardTransitionDuration(swipeState.swipeExitDirection, swipeState.swipeOffset);
    $[19] = swipeState.swipeExitDirection;
    $[20] = swipeState.swipeOffset;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== canUseSwiperTouchSurface || $[23] !== swipeState.handleCardTransitionEnd || $[24] !== swipeState.handleSwiperMove || $[25] !== swipeState.handleSwiperTouchEnd || $[26] !== swipeState.handleTouchSurfaceCancel || $[27] !== swipeState.handleTouchSurfaceEnd || $[28] !== swipeState.handleTouchSurfaceMove || $[29] !== swipeState.handleTouchSurfaceStart || $[30] !== swipeState.swipeOffset || $[31] !== t4 || $[32] !== t5) {
    t6 = {
      previewCardRef,
      canUseSwiperTouchSurface,
      cardTransform: t4,
      cardTransitionDuration: t5,
      handleTouchSurfaceCancel: swipeState.handleTouchSurfaceCancel,
      handleTouchSurfaceEnd: swipeState.handleTouchSurfaceEnd,
      handleTouchSurfaceMove: swipeState.handleTouchSurfaceMove,
      handleTouchSurfaceStart: swipeState.handleTouchSurfaceStart,
      swipeOffset: swipeState.swipeOffset,
      handleCardTransitionEnd: swipeState.handleCardTransitionEnd,
      handleSwiperMove: swipeState.handleSwiperMove,
      handleSwiperTouchEnd: swipeState.handleSwiperTouchEnd
    };
    $[22] = canUseSwiperTouchSurface;
    $[23] = swipeState.handleCardTransitionEnd;
    $[24] = swipeState.handleSwiperMove;
    $[25] = swipeState.handleSwiperTouchEnd;
    $[26] = swipeState.handleTouchSurfaceCancel;
    $[27] = swipeState.handleTouchSurfaceEnd;
    $[28] = swipeState.handleTouchSurfaceMove;
    $[29] = swipeState.handleTouchSurfaceStart;
    $[30] = swipeState.swipeOffset;
    $[31] = t4;
    $[32] = t5;
    $[33] = t6;
  } else {
    t6 = $[33];
  }
  return t6;
}